import {
  FileSnapshot,
  DrawingSignedUrl,
  ImageSignedUrl,
  MergedSignedUrl,
  MergedThumbnailSignedUrl,
  SignedUrl,
  RawSignedRequestSnapshot,
  SignedRequestSnapshot,
  ThumbnailSignedUrl,
} from './types';
import { maybe } from 'shared/types/brand';
import { DocumentUrlsResult } from 'shared/domain/document/documentUrlsModel';
import { fileKindService } from '../fileKindService';
import {
  isDisplayableNonPdfSource,
  isPersistedSource,
} from 'shared/utils/sourceUrl';
import { isBlobUrl } from 'shared/utils/blobUrl';
import { sanitizeFileName, generateFallbackFileName } from 'shared/utils/signedUrlFileUtils';

export class SignedRequestService {
  private toBrandedSignedRequests(
    response: DocumentUrlsResult
  ): SignedRequestSnapshot {
    const urls = response.documentUrls;

    return {
      imageSignedRequest: maybe(ImageSignedUrl, urls.imageSignedRequest),
      drawingSignedRequest: maybe(
        DrawingSignedUrl,
        urls.drawingSignedRequest
      ),
      mergedSignedRequest: maybe(
        MergedSignedUrl,
        urls.mergedSignedRequest
      ),
      mergedThumbnailSignedRequest: maybe(
        MergedThumbnailSignedUrl,
        urls.mergedThumbnailSignedRequest
      ),
      signedRequest: SignedUrl(urls.signedRequest),
      thumbnailSignedRequest: maybe(
        ThumbnailSignedUrl,
        urls.thumbnailSignedRequest
      ),
    };
  }

  extractFileNameFromSignedRequest(
    signedRequest?: string
  ): string | undefined {
    if (!signedRequest) return undefined;
    try {
      const url = new URL(signedRequest);
      const params = url.searchParams;

      const disposition =
        params.get('response-content-disposition') ||
        params.get('content-disposition') ||
        params.get('Content-Disposition');

      if (disposition) {
        const decoded = decodeURIComponent(disposition);
        const regexFilenameStar = /filename\*\s*=\s*(?:UTF-8''?)?"?([^";]*)"?/i;
        const filenameStarMatch = decoded.match(regexFilenameStar);
        if (filenameStarMatch && filenameStarMatch[1]) {
          return sanitizeFileName(decodeURIComponent(filenameStarMatch[1]));
        }

        const regexFilename = /filename\s*=\s*"?([^";]*)"?/i;
        const filenameMatch = decoded.match(regexFilename);
        if (filenameMatch && filenameMatch[1]) {
          return sanitizeFileName(filenameMatch[1]);
        }
      }

      const filenameParams = ['filename', 'file', 'name', 'key', 'Key'];
      for (const param of filenameParams) {
        const value = params.get(param);
        if (value) return sanitizeFileName(decodeURIComponent(value));
      }

      const pathname = url.pathname || '';
      const lastSegment = pathname.split('/').filter(Boolean).pop();
      if (lastSegment) {
        const clean = lastSegment.split('?')[0].split('#')[0];
        return sanitizeFileName(decodeURIComponent(clean));
      }
    } catch {
      const matched =
        signedRequest.match(/filename\*?=([^&\s]+)/i) ||
        signedRequest.match(/name=([^&\s]+)/i);
      if (matched && matched[1]) {
        return sanitizeFileName(
          decodeURIComponent(matched[1].replace(/^UTF-8''/i, ''))
        );
      }
      const pathMatch = signedRequest.match(/\/([^\/\s?]+)(?:\?|$)/);
      if (pathMatch && pathMatch[1]) {
        return sanitizeFileName(decodeURIComponent(pathMatch[1]));
      }
    }

    return undefined;
  }

  extractExtensionFromFileName(name?: string): string | undefined {
    if (!name) return undefined;
    const index = name.lastIndexOf('.');
    if (index === -1) return undefined;
    return name.slice(index + 1).toLowerCase();
  }

  getNameAndExtensionFromSignedRequest(
    signedRequest?: string,
    fallbackBase?: string
  ): { name: string; extension?: string } {
    const name =
      this.extractFileNameFromSignedRequest(signedRequest) ||
      fallbackBase ||
      `${generateFallbackFileName(signedRequest)}.pdf`;
    const extension = this.extractExtensionFromFileName(name) || undefined;

    return { name, extension };
  }

  hasMergedBackgroundSource(
    file: FileSnapshot,
    drawing: { mergedImageUrl?: string; clear?: boolean } | undefined
  ): boolean {
    return Boolean(
      drawing?.mergedImageUrl ||
        file.data?.mergedSrc ||
        file.signedRequest?.mergedSignedRequest ||
        file.signedRequest?.drawingSignedRequest
    );
  }

  resolvePostClearDownloadSource(file: FileSnapshot): string | undefined {
    return file.signedRequest?.signedRequest || file.downloadSrc;
  }

  resolveAttachmentSourcesByPriority(
    isDrawn: boolean | undefined,
    signedRequests: Pick<
      SignedRequestSnapshot,
      | 'signedRequest'
      | 'thumbnailSignedRequest'
      | 'mergedSignedRequest'
      | 'mergedThumbnailSignedRequest'
    >
  ): (string | undefined)[] {
    const {
      signedRequest,
      thumbnailSignedRequest,
      mergedSignedRequest,
      mergedThumbnailSignedRequest,
    } = signedRequests;

    if (isDrawn) {
      return [
        mergedThumbnailSignedRequest,
        mergedSignedRequest,
        thumbnailSignedRequest,
        signedRequest,
      ];
    }

    return [thumbnailSignedRequest, signedRequest];
  }

  shouldUpdateSignedRequest(
    file: {
      type?: string;
      data?: { isDrawn?: boolean; extension?: string };
      signedRequest?: Pick<
        RawSignedRequestSnapshot,
        | 'imageSignedRequest'
        | 'signedRequest'
        | 'mergedSignedRequest'
        | 'thumbnailSignedRequest'
        | 'mergedThumbnailSignedRequest'
      >;
    },
    response: DocumentUrlsResult
  ): boolean {
    const urls = this.toBrandedSignedRequests(response);
    const hasNoSignedRequest = !file.signedRequest;
    const responseHasImageSignedRequest = !!urls.imageSignedRequest;
    const currentFileNeedsImageSignedRequest =
      this.isPdfNeedingImageSignedRequest(file);

    return (
      hasNoSignedRequest ||
      (currentFileNeedsImageSignedRequest && responseHasImageSignedRequest) ||
      this.hasNewImageSignedRequest(file, urls) ||
      this.hasNewThumbnailSignedRequest(file, urls) ||
      this.hasNewMergedThumbnailSignedRequest(file, urls) ||
      this.hasNewSignedRequest(file, urls) ||
      this.hasNewMergedSignedRequest(file, urls)
    );
  }

  private hasNewThumbnailSignedRequest(
    file: {
      signedRequest?: Pick<RawSignedRequestSnapshot, 'thumbnailSignedRequest'>;
    },
    signedRequests: Pick<SignedRequestSnapshot, 'thumbnailSignedRequest'>
  ): boolean {
    return (
      !!signedRequests.thumbnailSignedRequest &&
      !file.signedRequest?.thumbnailSignedRequest
    );
  }

  private hasNewMergedThumbnailSignedRequest(
    file: {
      signedRequest?: Pick<
        RawSignedRequestSnapshot,
        'mergedThumbnailSignedRequest'
      >;
    },
    signedRequests: Pick<SignedRequestSnapshot, 'mergedThumbnailSignedRequest'>
  ): boolean {
    return (
      !!signedRequests.mergedThumbnailSignedRequest &&
      !file.signedRequest?.mergedThumbnailSignedRequest
    );
  }

  resolveDocumentOriginalSource(
    response: DocumentUrlsResult
  ): string | undefined {
    const urls = this.toBrandedSignedRequests(response);
    return (
      urls.imageSignedRequest ||
      urls.signedRequest
    );
  }

  resolveDocumentDownloadSource(
    file: {
      type?: string;
      data?: { isDrawn?: boolean; extension?: string };
    },
    response: DocumentUrlsResult
  ): string | undefined {
    const urls = this.toBrandedSignedRequests(response);
    if (
      file.data?.isDrawn &&
      (urls.mergedSignedRequest || urls.drawingSignedRequest)
    ) {
      return urls.mergedSignedRequest || urls.drawingSignedRequest;
    }

    if (fileKindService.isPdf(file)) {
      return urls.signedRequest;
    }

    return urls.imageSignedRequest || urls.signedRequest;
  }

  resolveDocumentThumbnailSource(
    file: {
      type?: string;
      data?: { extension?: string; isDrawn?: boolean };
    },
    response: DocumentUrlsResult
  ): string | undefined {
    const urls = this.toBrandedSignedRequests(response);

    if (file.data?.isDrawn && urls.mergedThumbnailSignedRequest) {
      return urls.mergedThumbnailSignedRequest;
    }

    if (fileKindService.isPdf(file)) {
      return (
        urls.thumbnailSignedRequest ||
        urls.drawingSignedRequest ||
        // Converted page image — OK for PDF list tiles (not a full camera photo).
        urls.imageSignedRequest
      );
    }

    return (
      urls.thumbnailSignedRequest ||
      urls.drawingSignedRequest ||
      urls.imageSignedRequest
    );
  }

  resolveOriginalPdfDownloadSource(file: FileSnapshot): string | undefined {
    if (isPersistedSource(file.signedRequest?.signedRequest)) {
      return file.signedRequest.signedRequest;
    }

    if (isPersistedSource(file.downloadSrc)) {
      return file.downloadSrc;
    }

    return undefined;
  }

  resolveGalleryDownloadSource(
    file: FileSnapshot,
    mergedImageUrl: string | undefined
  ): string | undefined {
    // Converted PDF is previewed as an image, but download must stay the original PDF.
    if (fileKindService.isConvertedPdf(file)) {
      return this.resolveOriginalPdfDownloadSource(file);
    }

    return mergedImageUrl || this.resolveFileDownloadSource(file);
  }

  resolveFileDownloadSource(
    file: Pick<FileSnapshot, 'downloadSrc' | 'signedRequest'>
  ): string | undefined {
    return file.downloadSrc || file.signedRequest?.signedRequest;
  }

  hasResolvedDownloadSource(
    file: Pick<FileSnapshot, 'downloadSrc' | 'signedRequest'>
  ): boolean {
    return Boolean(this.resolveFileDownloadSource(file));
  }

  /**
   * Resolves thumbnail URL from signed request URLs.
   * Uses thumbnailSignedRequest or imageSignedRequest as fallback.
   */
  resolveThumbnailUrlFromSignedRequest(
    urls: Pick<
      RawSignedRequestSnapshot,
      'thumbnailSignedRequest' | 'imageSignedRequest'
    >
  ): string | undefined {
    return urls.thumbnailSignedRequest || urls.imageSignedRequest;
  }

  private isPdfNeedingImageSignedRequest(file: {
    type?: string;
    data?: { extension?: string };
    signedRequest?: Pick<RawSignedRequestSnapshot, 'imageSignedRequest'>;
  }): boolean {
    const isPdf = fileKindService.isPdf(file);
    const hasSignedRequest = !!file.signedRequest;
    const hasImageSignedRequest = !!file.signedRequest?.imageSignedRequest;

    return isPdf && hasSignedRequest && !hasImageSignedRequest;
  }

  private hasNewImageSignedRequest(
    file: {
      signedRequest?: Pick<RawSignedRequestSnapshot, 'imageSignedRequest'>;
    },
    signedRequests: Pick<SignedRequestSnapshot, 'imageSignedRequest'>
  ): boolean {
    return (
      !!signedRequests.imageSignedRequest &&
      !file.signedRequest?.imageSignedRequest
    );
  }

  private hasNewSignedRequest(
    file: {
      signedRequest?: Pick<RawSignedRequestSnapshot, 'signedRequest'>;
    },
    signedRequests: Pick<SignedRequestSnapshot, 'signedRequest'>
  ): boolean {
    return (
      !!signedRequests.signedRequest &&
      !file.signedRequest?.signedRequest
    );
  }

  private hasNewMergedSignedRequest(
    file: {
      data?: { isDrawn?: boolean };
      signedRequest?: Pick<RawSignedRequestSnapshot, 'mergedSignedRequest'>;
    },
    signedRequests: Pick<SignedRequestSnapshot, 'mergedSignedRequest'>
  ): boolean {
    const currentMerged = file.signedRequest?.mergedSignedRequest;
    const newMerged = signedRequests.mergedSignedRequest;

    if (file.data?.isDrawn && newMerged) {
      if (!currentMerged) {
        return true;
      }

      if (
        isBlobUrl(currentMerged) &&
        !isBlobUrl(newMerged)
      ) {
        return true;
      }

      if (currentMerged !== newMerged) {
        return true;
      }
    }

    return false;
  }

  resolveDrawInitialSource(
    file: FileSnapshot,
    drawing: {
      mergedImageUrl?: string;
      clear?: boolean;
    } | undefined,
    cleared: boolean
  ): string | undefined {
    // Resolve initial source for draw mode.
    // Priority: cleared base image -> existing merged state -> converted PDF image -> fallback original.
    const isConvertedPdf = fileKindService.isConvertedPdf(file);

    const baseImageSource =
      file.signedRequest?.imageSignedRequest ||
      file.signedRequest?.signedRequest ||
      file.downloadSrc;
    const mergedImageUrl = drawing?.mergedImageUrl;
    const currentMergedSource =
      (isDisplayableNonPdfSource(mergedImageUrl) &&
      !isBlobUrl(mergedImageUrl)
        ? mergedImageUrl
        : undefined) ||
      file.signedRequest?.mergedSignedRequest ||
      file.data?.mergedSrc ||
      file.signedRequest?.drawingSignedRequest;

    if (cleared) {
      return baseImageSource;
    }

    if (currentMergedSource) {
      return currentMergedSource;
    }

    if (isConvertedPdf) {
      return file.signedRequest?.imageSignedRequest;
    }

    // Prefer signed URLs over downloadSrc — stale blob downloadSrc breaks draw mode.
    return baseImageSource || this.resolveFileDownloadSource(file);
  }

  resolveDrawClearedPreviewSource(file: FileSnapshot): string | undefined {
    // Resolve preview source right after clearing drawing.
    // Priority: converted PDF image -> generated thumbnail -> persisted thumbnail field.
    // This favors fast visual feedback while full originals may still be loading.
    return (
      file.signedRequest?.imageSignedRequest ||
      file.signedRequest?.thumbnailSignedRequest ||
      file.thumbnail
    );
  }

  resolveDrawOriginalSource(
    file: FileSnapshot,
    cleared: boolean
  ): string | undefined {
    // Resolve source used as base for drawing merge.
    // Priority for converted PDF: generated image source only.
    // Priority otherwise: when cleared use signed URLs first, else prefer current downloadable source.
    const isConvertedPdf = fileKindService.isConvertedPdf(file);

    if (isConvertedPdf) {
      return file.signedRequest?.imageSignedRequest;
    }

    const signedFallback =
      file.signedRequest?.mergedSignedRequest ||
      file.signedRequest?.imageSignedRequest ||
      file.signedRequest?.signedRequest;

    return cleared
      ? signedFallback || file.downloadSrc
      : file.downloadSrc || signedFallback;
  }

  resolveFullPreviewSource(
    storedBlobUrl: string | null | undefined,
    drawing: {
      mergedImageUrl?: string;
      clear?: boolean;
    } | undefined,
    file: FileSnapshot
  ): string | undefined {
    // If drawing is explicitly cleared, show the underlying original first.
    // Prefer full sources over thumbnails to avoid low-resolution preview.
    if (drawing?.clear) {
      if (file.signedRequest?.imageSignedRequest) {
        return file.signedRequest.imageSignedRequest;
      }

      if (file.signedRequest?.signedRequest) {
        return file.signedRequest.signedRequest;
      }

      if (isDisplayableNonPdfSource(file.src)) {
        return file.src;
      }

      if (isDisplayableNonPdfSource(file.downloadSrc)) {
        return file.downloadSrc;
      }

      return undefined;
    }

    // Local blob (freshly edited/merged image) should win while user is in session.
    if (storedBlobUrl) {
      return storedBlobUrl;
    }

    // After upload SUCCESS the SW may revoke session blobs; prefer signed merged URL.
    if (file.signedRequest?.mergedSignedRequest) {
      return file.signedRequest.mergedSignedRequest;
    }

    if (file.signedRequest?.drawingSignedRequest) {
      return file.signedRequest.drawingSignedRequest;
    }

    if (
      isDisplayableNonPdfSource(drawing?.mergedImageUrl) &&
      !isBlobUrl(drawing.mergedImageUrl)
    ) {
      return drawing.mergedImageUrl;
    }

    if (isPersistedSource(file.data?.mergedSrc)) {
      return file.data.mergedSrc;
    }

    if (isPersistedSource(file.data?.drawingSrc)) {
      return file.data.drawingSrc;
    }

    if (file.signedRequest?.imageSignedRequest) {
      return file.signedRequest.imageSignedRequest;
    }

    if (
      fileKindService.isPdf(file) &&
      file.signedRequest?.thumbnailSignedRequest
    ) {
      return file.signedRequest.thumbnailSignedRequest;
    }

    // For regular images, prefer full original signed request over thumbnail URL
    // to prevent blurry preview after page reload.
    if (isDisplayableNonPdfSource(file.signedRequest?.signedRequest)) {
      return file.signedRequest.signedRequest;
    }

    if (isDisplayableNonPdfSource(file.signedRequest?.thumbnailSignedRequest)) {
      return file.signedRequest.thumbnailSignedRequest;
    }

    if (isDisplayableNonPdfSource(file.src)) {
      return file.src;
    }

    if (isDisplayableNonPdfSource(file.downloadSrc)) {
      return file.downloadSrc;
    }

    return undefined;
  }

}

export const signedRequestService = new SignedRequestService();

export type { FileSnapshot, SignedRequestSnapshot } from './types';
